import React, { useMemo, useState } from 'react';
import { Table, Badge, Progress, Avatar, Space, Skeleton } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Product } from '../../models/Product';
import { FaAngleRight, FaArrowRight, FaEye, FaPen, } from 'react-icons/fa6';
import QuickEditModal from '../quickEditModal/QuickEditModal';

interface Props {
  products: Product[];
  pageSize: number;
  current: number;
  total: number;
  loading: boolean; 
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onRefresh: () => void; // Para que la modal avise que terminó
}

const ProductTable = ({ products, pageSize, current, total, loading, setPage, onRefresh }: Props) => {
const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);


  const openEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Datos ficticios para el Skeleton
  const skeletonData = useMemo(() => 
    Array.from({ length: pageSize }).map((_, i) => ({ id: `skeleton-${i}` } as unknown as Product)),
    [pageSize]
  );

  const columns: ColumnsType<Product> = useMemo(() => [
    {
      title: 'PRODUCT',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        loading ? (
          <Space size="middle">
            <Skeleton.Avatar active shape="square" size={48} />
            <Skeleton active title={false} paragraph={{ rows: 2, width: [150, 100] }} />
          </Space>
        ) : (
          <Space size="middle">
            <Avatar 
              shape="square" 
              size={48} 
              src={record.image} 
              style={{ backgroundColor: '#fdf3f0', padding: '4px' }} 
            />
            <div>
              <div style={{ fontWeight: 600, color: '#1a1a1a' }}>{record.name}</div>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>ID: {record.id}</div>
            </div>
          </Space>
        )
      ),
    },
    {
      title: 'CATEGORY',
      dataIndex: 'category',
      key: 'category',
      render: (_, record) => (
        loading ? <Skeleton.Button active size="small" style={{ width: 80, borderRadius: 12 }} /> : (
          <Badge 
            count={record.category?.name || 'N/A'} 
            style={{ 
              backgroundColor: "#f0f5ff", 
              color: '#595959', 
              fontWeight: 500,
              borderRadius: '12px',
              padding: '0 10px'
            }} 
          />
        )
      ),
    },
      {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
      render: (_, record) => (
        loading ? <Skeleton.Button active size="small" style={{ width: 80, borderRadius: 12 }} /> : (
          <Badge 
            count={record.location || 'N/A'} 
            style={{ 
              backgroundColor: "#f0f5ff", 
              color: '#595959', 
              fontWeight: 500,
              borderRadius: '12px',
              padding: '0 10px'
            }} 
          />
        )
      ),
    },
    {
      title: 'PRICE',
      dataIndex: 'bcvPrice',
      key: 'price',
      render: (price, record) => (
        loading ? <Skeleton.Input active size="small" style={{ width: 60 }} /> : (
          <span style={{ fontWeight: 600 }}>${(price || 0).toFixed(2)}</span>
        )
      ),
    },
    {
      title: 'STOCK LEVEL',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity, record) => (
        loading ? <Skeleton.Input active size="small" style={{ width: 100 }} /> : (
          <div style={{ width: 100 }}>
            <div style={{ fontWeight: 'bold', fontStyle: 'italic', marginBottom: 4 }}>
              {quantity}
            </div>
            <Progress 
              percent={Math.min((quantity / 50) * 100, 100)} 
              showInfo={false} 
              strokeColor={quantity < 10 ? '#f56a00' : '#52c41a'} 
              size="small"
              strokeWidth={6}
            />
          </div>
        )
      ),
    },
{
      title: 'ACTIONS',
      key: 'actions',
      render: (_, record) => loading ? null : (
        <Space size="middle">
          <FaPen
            style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: '18px' }} 
            onClick={() => openEdit(record)} // Abrimos la modal pasándole el objeto
            title="Edición rápida"
          />
          <FaAngleRight style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: '18px' }}  />
        </Space>
      ),
    }
  ], [loading]);

  const changePage = (pagination: any) => {
    if (pagination.current) {
      setPage(pagination.current);
    }
  };

  return (
    <>
    <Table 
      columns={columns} 
      dataSource={loading ? skeletonData : products} 
      rowKey={(record) => record.id}
      pagination={{
        current: current,
        pageSize: pageSize,
        total: total,
        showSizeChanger: false,
        position: ['bottomRight'],
      }}
      onChange={changePage}
      className="custom-product-table"
    />
    
      <QuickEditModal 
        product={selectedProduct}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={onRefresh} // Refresca la tabla al terminar el PATCH
      />
    </>

  );
};

export default ProductTable;